import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import {
  lazy,
  type ReactNode,
  Suspense,
  useLayoutEffect,
  useState,
} from "react";
import { AppErrorBoundary } from "@/app/AppErrorBoundary";
import { LegacyDraftStorageCleanup } from "@/app/LegacyDraftStorageCleanup";
import { DesktopLayout } from "@/app/layouts/DesktopLayout";
import { MainContent } from "@/app/layouts/layoutAccessibility";
import { MobileLayout } from "@/app/layouts/MobileLayout";
import { SideMenuProvider } from "@/app/layouts/SideMenuProvider";
import { AnnouncementStatusProvider } from "@/features/announce/components/AnnouncementStatusProvider";
import { CatalogLayoutInitializer } from "@/features/catalog/components/CatalogLayoutInitializer";
import { shouldDeferCatalogLayout } from "@/features/catalog/utils/catalogLayoutRoute";
import { DockingOnboardingGate } from "@/features/mix-onboarding/components/DockingOnboardingGate";
import { usePlayerStore } from "@/features/player/stores/playerStore";
import { R18BlurOnboardingGate } from "@/features/r18-blur-onboarding/components/R18BlurOnboardingGate";
import { useSettingsStore } from "@/features/settings/hooks";
import { resolveLightMode } from "@/features/settings/stores/legacyDisplaySettings";
import { resolvePostFontFamily } from "@/features/settings/utils/renderSettings";
import { useIsDesktop, useSystemPrefersDark } from "@/shared/hooks";
import { useHasFinePointer } from "@/shared/hooks/useMediaQuery";
import { useOnlineUsersHeartbeat } from "@/shared/hooks/useOnlineUsersHeartbeat";
import { useRouteCanonical } from "@/shared/hooks/useRouteCanonical";
import { cn } from "@/shared/lib/cn";
import { ModalProvider } from "@/shared/ui/overlay";
import { ToastProvider } from "@/shared/ui/toast";

// MiniPlayer は再生開始時に初めてロード（motion/react や Player 周辺コードを初期 bundle に含めない）
const LazyMiniPlayer: React.LazyExoticComponent<React.ComponentType> = lazy(
  () =>
    import("@/features/player/ui/MiniPlayer").then((m) => ({
      default: m.MiniPlayer,
    })),
);

interface RootLayoutProps {
  children: ReactNode;
  isStandaloneJukebox: boolean;
}

/** standaloneなジュークボックスには告知UIとそのmeta pollをマウントしない。 */
export const RootLayout: React.FunctionComponent<RootLayoutProps> = ({
  children,
  isStandaloneJukebox,
}: RootLayoutProps) => {
  if (isStandaloneJukebox) return <>{children}</>;

  return (
    <AnnouncementStatusProvider>
      <SideMenuProvider>{children}</SideMenuProvider>
    </AnnouncementStatusProvider>
  );
};

const RootComponent: React.FunctionComponent = () => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );

  useRenderSettings();

  const privacyMode = useSettingsStore((s) => s.privacyMode);
  // MiniPlayer が一度でも開かれたかをトラッキング: 開いた後に閉じても chunk は既に DL 済み
  const miniPlayerVisible = usePlayerStore((s) => s.miniPlayer.visible);
  const isDesktop = useIsDesktop();
  const hasFinePointer = useHasFinePointer();
  const location = useLocation();
  useRouteCanonical(location.pathname);
  useOnlineUsersHeartbeat(location.pathname !== "/jukebox");
  // PC版のジュークボックスはヘッダー・サイドメニュー等のアプリ枠を付けず
  // ジュークボックス単独で表示する。standalone=1 は別ウィンドウ
  // (openJukeboxWindow)とそのフォールバック遷移が付けるマーカーで、
  // ポップアップ(幅440px、isDesktop=false)でもモバイル枠を外すために使う。
  // モバイル実機のサイドメニューからの /jukebox 遷移(マーカー無し)は
  // 従来どおりモバイル枠付きで表示する。
  const isStandaloneJukebox =
    location.pathname === "/jukebox" &&
    (isDesktop ||
      (location.search as { standalone?: boolean }).standalone === true);
  const deferCatalogLayout = shouldDeferCatalogLayout(location.pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <LegacyDraftStorageCleanup />
      <HeadContent />
      <div
        id="app-root"
        className={cn(
          "min-h-screen bg-background text-foreground",
          privacyMode && "privacy-vignette",
        )}
      >
        <ModalProvider fixedBodyScrollLock={!hasFinePointer}>
          <AppErrorBoundary>
            {/* 初回パネル中はカタログ側をマウントさせない(取得とサムネイルを止める) */}
            <DockingOnboardingGate
              isDesktop={isDesktop}
              pathname={location.pathname}
            >
              {/* /mixの初回パネルはchildrenごと差し替えるため、この位置なら
                  ぼかし案内モーダルはパネル完了後に初表示され、重ならない */}
              {!isStandaloneJukebox && <R18BlurOnboardingGate />}
              <CatalogLayoutInitializer
                deferChildren={deferCatalogLayout}
                isDesktop={isDesktop}
              >
                <RootLayout isStandaloneJukebox={isStandaloneJukebox}>
                  {isStandaloneJukebox ? (
                    <MainContent className="min-h-screen">
                      <Outlet />
                    </MainContent>
                  ) : isDesktop ? (
                    <DesktopLayout>
                      <Outlet />
                    </DesktopLayout>
                  ) : (
                    <MobileLayout>
                      <Outlet />
                    </MobileLayout>
                  )}
                </RootLayout>
              </CatalogLayoutInitializer>
            </DockingOnboardingGate>
            {miniPlayerVisible && (
              <Suspense fallback={null}>
                <LazyMiniPlayer />
              </Suspense>
            )}
          </AppErrorBoundary>
        </ModalProvider>
        <ToastProvider />
      </div>
    </QueryClientProvider>
  );
};

export const Route = createRootRoute({
  component: RootComponent,
});

/** 表示設定を反映する */
function useRenderSettings(): void {
  const darkMode = useSettingsStore((s) => s.darkMode);
  const globalFontScale = useSettingsStore((s) => s.globalFontScale);
  const postBodyFontSize = useSettingsStore((s) => s.postBodyFontSize);
  const fontFamily = useSettingsStore((s) => s.fontFamily);
  // darkModeが"system"のときのみresolveLightMode内で参照される。
  // OS設定の切替イベントを常時購読し、"system"選択中は即時反映する。
  // 実際のmatchMediaリスナーはアプリ全体で1本に共有される
  // （useSystemPrefersDark、Issue #50 レビュー指摘）
  const systemPrefersDark = useSystemPrefersDark();

  useLayoutEffect(() => {
    document.documentElement.classList.toggle(
      "light-mode",
      resolveLightMode(darkMode, systemPrefersDark),
    );
  }, [darkMode, systemPrefersDark]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "font-size",
      `${globalFontScale / 100}rem`,
    );
  }, [globalFontScale]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--post-body-font-size",
      `${postBodyFontSize}px`,
    );
  }, [postBodyFontSize]);

  useLayoutEffect(() => {
    const postFontFamily = resolvePostFontFamily(fontFamily);
    if (postFontFamily === null) {
      document.documentElement.style.removeProperty("--post-font-family");
      return;
    }
    document.documentElement.style.setProperty(
      "--post-font-family",
      postFontFamily,
    );
  }, [fontFamily]);
}
